import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "use", "orientation", "role", "onClick"];
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledButton, UnstyledButton } from '../Button/PrivateButton';
import { useIsKeyboardMode } from '../../../components/AppProvider/KeyboardModeProvider';
import { StyledButtonLink, UnstyledButtonLink } from '../Button/PrivateButtonLink';
import { MERCURY_LAYER, VENUS_LAYER } from '../../constants';
import { useFloatingButtonGroupContext } from '../FloatingButtonGroup/FloatingButtonGroupContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledButtonGroup = styled.div.withConfig({
  displayName: "ButtonGroup__StyledButtonGroup",
  componentId: "sc-1su6x74-0"
})(({
  $floatingGroup,
  $orientation,
  $srOnly,
  $use,
  $cssStyles,
  theme
}) => {
  const isKeyboardMode = useIsKeyboardMode();
  // when the parent is a FloatingButtonGroup, $floatingGroup is true and $use is 'default'
  const floating = $use === 'floating' || $floatingGroup;
  return Object.assign({
    display: $orientation === 'horizontal' ? 'inline-block' : 'inline-flex',
    flexDirection: $orientation === 'vertical' ? 'column' : undefined
  }, floating ? Object.assign({
    marginBlockStart: `-${theme.space['400']}`,
    paddingBlockStart: theme.space['400'],
    position: 'absolute',
    zIndex: MERCURY_LAYER
  }, $use === 'floating' ? {
    insetBlockStart: theme.space['0'],
    insetInlineEnd: theme.space['0'],
    insetInlineStart: theme.space['0']
  } : {
    insetBlockStart: `-${theme.space['600']}`,
    insetInlineEnd: theme.space['250']
  }) : undefined, $cssStyles, {
    [`${StyledButton}, ${UnstyledButton}, ${StyledButtonLink}, ${UnstyledButtonLink}`]: Object.assign({
      marginBlockStart: $orientation === 'horizontal' ? theme.space['0'] : undefined,
      marginInlineStart: $orientation === 'vertical' ? theme.space['0'] : undefined,
      position: 'relative',
      display: $orientation === 'vertical' ? 'block' : undefined
    }, floating ? {
      fontSize: theme.typography['detail-100'].fontSize,
      lineHeight: theme.typography['detail-100'].fontSize,
      // Using font size here since the font size and line height are the same
      maxHeight: '32px',
      paddingBlock: '9px',
      backgroundColor: theme.color['fill-accent-neutral-default'],
      borderBlockColor: theme.color['fill-accent-neutral-hover'],
      borderInlineColor: theme.color['fill-accent-neutral-hover'],
      color: theme.color['text-core-on-fill-default'],
      "&:not([aria-disabled='true'])": {
        '&:hover': {
          backgroundColor: theme.color['fill-accent-neutral-hover'],
          color: theme.color['text-core-on-fill-default'],
          borderBlockColor: theme.color['fill-accent-neutral-hover'],
          borderInlineColor: theme.color['fill-accent-neutral-hover']
        },
        "&:active, &[aria-pressed='true']": {
          backgroundColor: theme.color['fill-accent-neutral-pressed'],
          color: theme.color['text-core-on-fill-default'],
          borderBlockColor: theme.color['fill-accent-neutral-hover'],
          borderInlineColor: theme.color['fill-accent-neutral-hover']
        }
      }
    } : undefined, {
      '&:not(&:only-child)': {
        '&:first-of-type': {
          borderStartEndRadius: $orientation === 'horizontal' ? theme.borderRadius['0'] : undefined,
          borderEndStartRadius: $orientation === 'vertical' ? theme.borderRadius['0'] : undefined,
          borderEndEndRadius: theme.borderRadius['0']
        },
        '&:last-of-type': {
          borderStartStartRadius: theme.borderRadius['0'],
          borderStartEndRadius: $orientation === 'vertical' ? theme.borderRadius['0'] : undefined,
          borderEndStartRadius: $orientation === 'horizontal' ? theme.borderRadius['0'] : undefined
        },
        '&:not(&:first-of-type):not(&:last-of-type)': {
          borderStartStartRadius: theme.borderRadius['0'],
          borderStartEndRadius: theme.borderRadius['0'],
          borderEndEndRadius: theme.borderRadius['0'],
          borderEndStartRadius: theme.borderRadius['0']
        }
      },
      '&:not(&:first-child)': {
        marginInlineStart: $orientation === 'horizontal' ? '-1px' : undefined,
        // Doesn't need to be tokenized
        marginBlockStart: $orientation === 'vertical' ? '-1px' : undefined // Doesn't need to be tokenized
      },
      '&:focus': {
        zIndex: isKeyboardMode ? VENUS_LAYER : undefined
      },
      "&[aria-pressed='true']:not([aria-disabled='true'])": {
        zIndex: MERCURY_LAYER
      },
      "&[aria-disabled='true']": {
        borderColor: theme.color['border-core-default']
      }
    }),
    [`& ${StyledButton}[data-dropdown="true"]`]: {
      paddingBlock: theme.space['250']
    }
  }, $srOnly && {
    position: 'absolute',
    top: '0 !important',
    width: `${theme.space[25]} !important`,
    height: `${theme.space[25]} !important`,
    margin: `-${theme.space[25]} !important`,
    padding: '0 !important',
    overflow: 'hidden !important',
    clip: 'rect(0, 0, 0, 0) !important',
    border: '0 !important'
  });
});
export function ButtonGroup(_ref) {
  let {
      children,
      use = 'default',
      orientation = 'horizontal',
      role = 'group',
      onClick
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(restProps);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const floatingButtonGroupContext = useFloatingButtonGroupContext();
  const className = floatingButtonGroupContext.srOnly ? 'sr-only' : undefined;
  return /*#__PURE__*/_jsx(StyledButtonGroup, Object.assign({}, domProps, {
    className: className,
    role: role,
    onClick: onClick,
    $floatingGroup: floatingButtonGroupContext.floatingGroup,
    $srOnly: floatingButtonGroupContext.srOnly,
    $orientation: orientation,
    $use: use,
    $cssStyles: cssStyles,
    children: children
  }));
}